import dayjs from 'dayjs';
import { max, round, uniq } from 'lodash';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'tdesign-react';
import ShadowCard from '@/components/ShadowCard';
import SvgIcon from '@/components/SvgIcon';
import { convertAmount, convertByte, convertTime, isVoid } from '@/utils/common';
import LineChart from './LineChart';
import Modal from './Modal';
import AreaChart from './AreaChart';

export const formatter = 'YY-MM-DD HH:mm:ss';

export default function Chart({
  name,
  dataKey,
  description,
  unit: unitKey,
  isMultipleInstances, // 多维度
  chartsData,
  color,
  currentOrder, // 当前排序，数据量过大延迟渲染用
  colors,
  loading,
  onBrush,
}) {
  const [visible, setVisible] = useState(false);
  const isRate = unitKey === 'numRate' || unitKey === 'bytesRate';
  // 解析后的图表使用_data
  // groupLength:分组数量，优化slider使用
  // isGroup 是否分组判断（数据结构不一致兼容，部分使用isMultipleInstances === ‘true’ 部分在数据中使用存在key：instance）
  const {
    data: _data,
    length: groupLength,
    isGroup,
  } = useMemo(() => {
    const data = chartsData[dataKey] || [];
    let instance = []; // 组key
    // 多维转换
    // 形如[[],[]]或[,]
    const _data = data.map((item) => {
      if (isMultipleInstances) {
        const arr = [];
        // publishLatency 延时图因排序特殊处理
        if ('publishLatency' === dataKey) {
          [
            '0-0.5 ms',
            '0.5-1 ms',
            '1-5 ms',
            '5-10 ms',
            '10-20 ms',
            '20-50 ms',
            '50-100 ms',
            '100-200 ms',
            '200-1000 ms',
            '>1s',
          ]
            .reverse()
            .forEach((key) => {
              arr.push({
                time: item.time,
                value: item[key],
                seriesField: key,
              });
              instance.push(key);
            });
          return arr;
        }

        // kopTopicPublishLatency 延时图因排序特殊处理
        if ('kopTopicPublishLatency ' === dataKey) {
          ['50pct', '95pct', '99pct', '99_9pct'].forEach((key) => {
            arr.push({
              time: item.time,
              value: item[key],
              seriesField: key,
            });
            instance.push(key);
          });
          return arr;
        }

        for (const [key, val] of Object.entries(item)) {
          if (key !== 'time') {
            arr.push({
              time: item.time,
              value: val,
              seriesField: key,
            });
            instance.push(key);
          }
        }
        return arr;
      }
      // 如果存在instance，则认为是分组
      if (item.instance) {
        instance.push(item.instance);
        item.seriesField = item.instance;
      }
      return item;
    });

    instance = uniq(instance);
    const flatData = _data.flat();

    return {
      data: flatData,
      length: instance.length || 1,
      isGroup: instance.length > 0,
    };
    // }
  }, [chartsData, dataKey, isMultipleInstances]);

  const maxValue = max(_data.map((item) => item.value));

  // multiplier 乘数
  const { unit, multiplier } = useMemo(() => doParse(unitKey, maxValue || 0), [maxValue, unitKey]);

  const chartRenderData = useMemo(() => {
    return _data.map((item) => {
      return {
        ...item,
        timeStr: dayjs(item.time * 1000).format(formatter),
        time: item.time,
        valueFmt: isVoid(item.value) ? null : round(item.value / multiplier, 2), // null则坐标显示 0
        value: item.value,
      };
    });
  }, [_data, multiplier]);

  const unitText = useMemo(() => {
    if (unit) {
      return isRate ? `${unit}/s` : unit;
    }
    return unit;
  }, [unit, isRate]);

  const headerTitle = (
    <HeaderTitle>
      <Title>{name}</Title>
      <Unit>{unitText}</Unit>
      {description && (
        <Tooltip theme='light' content={<span dangerouslySetInnerHTML={{ __html: description }} />}>
          <span>
            <Icon type='exclamation-circle' />
          </span>
        </Tooltip>
      )}
    </HeaderTitle>
  );

  const chartProps = {
    data: chartRenderData,
    color,
    colors,
    unitKey,
    doParse,
    isGroup,
    isRate,
    groupLength,
    currentOrder,
    name,
    loading,
    onBrush,
  };

  const chart = ['publishLatency', 'kopTopicPublishLatency'].includes(dataKey) ? (
    <AreaChart {...chartProps} />
  ) : (
    <LineChart {...chartProps} />
  );

  return (
    <Root className='chart'>
      <Header>
        {headerTitle}
        <Icon onClick={() => setVisible(true)} size={18} type='zoom-in' />
      </Header>
      {chart}
      {visible && (
        <Modal
          header={headerTitle}
          name={name}
          unit={unitText}
          handleCancel={() => setVisible(false)}
          footer={null}
        >
          <Body>{chart}</Body>
        </Modal>
      )}
    </Root>
  );
}

// 单位转换
const doParse = (unitKey, value) => {
  let generate;
  const isByte = unitKey === 'bytes' || unitKey === 'byte' || unitKey === 'bytesRate';
  const isTime = unitKey === 'time';
  generate = convertAmount(value);
  if (isByte) {
    generate = convertByte(value);
  }
  if (isTime) {
    generate = convertTime(value);
  }
  return generate;
};

const Root = styled(ShadowCard)`
  .shadow-card-body {
    height: 266px;
    margin-bottom: 16px;
    padding: 20px 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }
  .chart-tool-tip {
    padding: 8px 4px;
    > div {
      padding: 2px 0;
      font-size: 12px;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const HeaderTitle = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const Title = styled.span`
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
  padding-right: 3px;
  color: #0d111a;
`;

const Unit = styled.span`
  font-size: 12px;
  line-height: 17px;
  color: #0d111a;
`;

const Icon = styled(SvgIcon)`
  color: var(--td-font-gray-2);
  cursor: pointer;
  font-size: 16px;
  padding: 0 6px;
  margin-top: -2px;
`;

const Body = styled.div`
  height: 50vh;
`;
